import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCountry } from '../../contexts/CountryContext';
import { useTenantScope } from '../../contexts/TenantScopeContext';
import { tenantsApi } from '../../lib/api';

function Initials({ name }) {
  const i = (name || '?')
    .split(' ')
    .map((w) => w[0] || '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return <span>{i}</span>;
}

export default function Topbar({ liveBadge = true, agentCount = null, showBack = false, backLabel = 'Retour au tableau de bord' }) {
  const { profile, signOut } = useAuth();
  const { country, countryCode, setCountry } = useCountry();
  const { scopeTenant, setScopeTenant } = useTenantScope();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const [tenantMenuOpen, setTenantMenuOpen] = useState(false);
  const [tenants, setTenants] = useState([]);
  const menuRef = useRef();

  const fullName = profile?.full_name || profile?.email || '';

  useEffect(() => {
    if (profile?.role !== 'superadmin') return;
    tenantsApi.list().then(({ data }) => setTenants(data || [])).catch(() => {});
  }, [profile?.role]);

  // Auto-detect: show back button on every page except '/'
  const isHome = location.pathname === '/';
  const shouldShowBack = showBack || !isHome;

  useEffect(() => {
    const onClick = (e) => {
      if (!e.target.closest('[data-menu-root]')) {
        setMenuOpen(false);
        setCountryMenuOpen(false);
        setTenantMenuOpen(false);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const handleLogout = async () => {
    setMenuOpen(false);
    setCountryMenuOpen(false);
    try {
      await signOut();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('signOut error', e);
    }
    // Hard replace so React Router state cannot bounce back to /
    window.location.replace('/login');
  };

  const role = profile?.role;
  const isSuper = role === 'superadmin';

  return (
    <header
      className="h-14 flex items-center justify-between px-5 border-b text-white"
      style={{ background: country.palette.primary, borderColor: country.palette.primaryHover }}
      data-testid="topbar"
    >
      <div className="flex items-center gap-3">
        {shouldShowBack && (
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition border border-white/30 hover:bg-white/20"
            style={{ background: 'rgba(255,255,255,.12)', color: '#fff' }}
            data-testid="topbar-back"
            title={backLabel}
          >
            <span className="text-base leading-none">←</span>
            <span>Dashboard</span>
          </Link>
        )}
        <Link to="/" className="flex items-center gap-2 font-semibold text-base" data-testid="topbar-brand">
          <span className="text-lg">{country.code === 'quebec' ? '🍁' : '🌍'}</span>
          <span>{country.brand}</span>
          <span className="text-[11px] font-normal" style={{ color: country.palette.light }}>
            v1.0
          </span>
        </Link>
      </div>

      <div className="flex items-center gap-3">
        {profile?.is_demo && (
          <span className="rounded-full px-3 py-1 text-xs font-bold" style={{ background: '#FFD60A', color: '#1a1a1a' }} data-testid="demo-badge" title="Compte de démonstration en lecture seule">
            🔒 DÉMO — lecture seule
          </span>
        )}
        {liveBadge && (
          <div className="flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-full animate-pulse"
              style={{ background: '#2DC653' }}
            />
            <span className="text-xs" style={{ color: country.palette.light }}>
              Temps réel
            </span>
          </div>
        )}
        {agentCount !== null && (
          <div
            className="rounded-full px-3 py-1 text-xs"
            style={{ background: 'rgba(255,255,255,.1)', border: '0.5px solid rgba(255,255,255,.2)' }}
            data-testid="topbar-agent-count"
          >
            {agentCount} agent{agentCount > 1 ? 's' : ''} actif{agentCount > 1 ? 's' : ''}
          </div>
        )}

        {/* Country switcher (visible to superadmin only) */}
        {isSuper && (
          <div className="relative" data-menu-root>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setCountryMenuOpen((v) => !v);
                setMenuOpen(false);
              }}
              className="rounded-full px-3 py-1 text-xs hover:bg-white/10 transition"
              style={{ background: 'rgba(255,255,255,.1)', border: '0.5px solid rgba(255,255,255,.2)' }}
              data-testid="country-switcher-btn"
            >
              {country.code === 'quebec' ? '🍁 QC' : '🌍 SN'} ▾
            </button>
            {countryMenuOpen && (
              <div
                className="absolute right-0 mt-2 w-44 bg-white text-gray-900 rounded-xl shadow-xl overflow-hidden z-50 border border-gray-200"
                data-testid="country-menu"
              >
                <button
                  onClick={() => {
                    setCountry('quebec');
                    setCountryMenuOpen(false);
                  }}
                  className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 ${
                    countryCode === 'quebec' ? 'bg-blue-50 font-medium' : ''
                  }`}
                  data-testid="country-option-quebec"
                >
                  🍁 Québec
                </button>
                <button
                  onClick={() => {
                    setCountry('senegal');
                    setCountryMenuOpen(false);
                  }}
                  className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 border-t border-gray-100 ${
                    countryCode === 'senegal' ? 'bg-green-50 font-medium' : ''
                  }`}
                  data-testid="country-option-senegal"
                >
                  🌍 Sénégal
                </button>
              </div>
            )}
          </div>
        )}

        {isSuper && (
          <div className="relative" data-menu-root>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setTenantMenuOpen((v) => !v);
                setCountryMenuOpen(false);
                setMenuOpen(false);
              }}
              className="rounded-full px-3 py-1 text-xs hover:bg-white/10 transition max-w-[170px] truncate"
              style={{ background: 'rgba(255,255,255,.1)', border: '0.5px solid rgba(255,255,255,.2)' }}
              data-testid="tenant-scope-btn"
              title="Filtrer le tableau de bord / la carte par tenant"
            >
              🏛️ {scopeTenant ? scopeTenant.nom : 'Tous les tenants'} ▾
            </button>
            {tenantMenuOpen && (
              <div className="absolute right-0 mt-2 w-64 max-h-80 overflow-y-auto bg-white text-gray-900 rounded-xl shadow-xl z-50 border border-gray-200" data-testid="tenant-scope-menu">
                <button
                  onClick={() => { setScopeTenant(null); setTenantMenuOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 ${!scopeTenant ? 'bg-blue-50 font-medium' : ''}`}
                  data-testid="tenant-scope-all"
                >
                  🌐 Tous les tenants
                </button>
                {tenants.map((tn) => (
                  <button
                    key={tn.id}
                    onClick={() => { setScopeTenant(tn); if (tn.country_mode) setCountry(tn.country_mode); setTenantMenuOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-b-0 ${scopeTenant?.id === tn.id ? 'bg-blue-50' : ''}`}
                    data-testid={`tenant-scope-${tn.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm truncate font-medium">{tn.nom}</span>
                      <span className="text-[11px]">{tn.country_mode === 'senegal' ? '🌍' : '🍁'}</span>
                    </div>
                    {tn.commune_nom && <div className="text-[10px] text-gray-400">{tn.commune_nom}</div>}
                  </button>
                ))}
                {tenants.length === 0 && <div className="px-4 py-3 text-xs text-gray-400">Aucun tenant</div>}
              </div>
            )}
          </div>
        )}

        <Link to="/carte" className="text-xs px-3 py-1.5 rounded-lg hover:bg-white/10 transition" style={{ background: 'rgba(255,255,255,.1)', border: '0.5px solid rgba(255,255,255,.2)' }} data-testid="nav-carte">
          🗺️ Carte
        </Link>
        {(isSuper || role === 'admin_region' || role === 'admin_commune') && (
          <Link to="/gestion" className="text-xs px-3 py-1.5 rounded-lg hover:bg-white/10 transition" style={{ background: 'rgba(255,255,255,.1)', border: '0.5px solid rgba(255,255,255,.2)' }} data-testid="nav-gestion">
            📋 Gestion
          </Link>
        )}
        {(role === 'admin_region' || role === 'admin_commune') && (
          <Link to="/mon-tenant" className="text-xs px-3 py-1.5 rounded-lg hover:bg-white/10 transition" style={{ background: 'rgba(255,255,255,.1)', border: '0.5px solid rgba(255,255,255,.2)' }} data-testid="nav-mon-tenant">
            🏢 Mon organisation
          </Link>
        )}
        {(role === 'agent' || role === 'admin_region' || role === 'admin_commune' || isSuper) && (
          <Link to="/agent" className="text-xs px-3 py-1.5 rounded-lg hover:bg-white/10 transition" style={{ background: 'rgba(255,255,255,.1)', border: '0.5px solid rgba(255,255,255,.2)' }} data-testid="nav-agent">
            📱 Agent
          </Link>
        )}
        {isSuper && (
          <>
            <Link to="/admin" className="text-xs px-3 py-1.5 rounded-lg hover:bg-white/10 transition" style={{ background: 'rgba(255,255,255,.1)', border: '0.5px solid rgba(255,255,255,.2)' }} data-testid="nav-admin">
              ⚙️ Admin
            </Link>
            <Link to="/tenants" className="text-xs px-3 py-1.5 rounded-lg hover:bg-white/10 transition" style={{ background: 'rgba(255,255,255,.1)', border: '0.5px solid rgba(255,255,255,.2)' }} data-testid="nav-tenants">
              🏛️ Tenants
            </Link>
          </>
        )}

        <div className="relative ml-1" data-menu-root ref={menuRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
              setCountryMenuOpen(false);
            }}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold border-2 border-white/30"
            style={{ background: country.palette.primaryHover }}
            data-testid="topbar-avatar"
          >
            <Initials name={fullName} />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 mt-2 w-56 bg-white text-gray-900 rounded-xl shadow-xl overflow-hidden z-50 border border-gray-200"
              data-testid="topbar-menu"
            >
              <div className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-100">
                {profile?.role === 'superadmin' ? 'Super-administrateur' : fullName}
              </div>
              <div className="px-4 py-2 text-xs text-gray-700 border-b border-gray-100 truncate">
                {fullName}
              </div>
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50"
                data-testid="topbar-logout"
              >
                ⬅️ Déconnexion
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
